import { scheduleBoot } from "./3os-living-r1c-p1-boot-gate.js";
/* ===========================================================================
   cinematic-205f.js · 205F — Cinematic Unity & Product Lust
   ---------------------------------------------------------------------------
   Two additive, decorative behaviours. This file NEVER touches the 205E video
   controller: it does not read, set or clear `manualPaused`, it does not call
   play()/pause(), it does not attach or release a source, and it does not touch
   the global control, its `aria-pressed`, the in-frame recovery CTA, the cart,
   the selector, the loader or the copy modes.

   1. LIVING LABEL — mounts one decorative <picture> per film frame, carrying
      that scene's already approved illustrated master from `assets/scenes/`.
      CSS shows it only while the frame publishes `data-video-state="playing"`.
      It is mounted lazily, so a visitor who never reaches a scene never pays
      for its master, and no-JS never receives it at all.

   2. GOLDEN TRACE — one decorative gold hairline drawn once in the Hero, the
      Relay and the Finale. transform + opacity only.
   =========================================================================== */

const $$ = (selector, scope = document) => [...(scope?.querySelectorAll(selector) ?? [])];

/* Scene key -> the scene's own illustrated master, desktop and portrait.
   Every file already exists in `assets/scenes/` and was approved in the R3 /
   201I passes. Nothing is generated, edited or re-encoded here.

   Each URL is written out in full, exactly like `cinemaAssets` in main.js. A
   template literal inside `new URL(..., import.meta.url)` cannot be resolved
   statically, so the bundler falls back to globbing the whole directory — that
   pulled all eighteen 3.4 MB PNG originals into the build and took it from
   43 MB to 108 MB. Literals keep the bundle to exactly what is used. */
const LIVING_LABEL = {
  tennis: {
    desktop: new URL("./3os-living-r1c-scenes-01-tennis-oil-desktop.webp", import.meta.url).href,
    mobile: new URL("./3os-living-r1c-scenes-01-tennis-oil-mobile.webp", import.meta.url).href,
  },
  flight: {
    desktop: new URL("./3os-r2-flight-desktop.webp", import.meta.url).href,
    mobile: new URL("./3os-r2-flight-mobile.webp", import.meta.url).href,
  },
  relay: {
    desktop: new URL("./3os-r2-relay-desktop.webp", import.meta.url).href,
    mobile: new URL("./3os-r2-relay-mobile.webp", import.meta.url).href,
  },
  boxer: {
    desktop: new URL("./3os-r2-boxer-desktop.webp", import.meta.url).href,
    mobile: new URL("./3os-r2-boxer-mobile.webp", import.meta.url).href,
  },
  ritual: {
    desktop: new URL("./3os-r2-ritual-desktop.webp", import.meta.url).href,
    mobile: new URL("./3os-r2-ritual-mobile.webp", import.meta.url).href,
  },
  society: {
    desktop: new URL("./3os-r2-society-desktop.webp", import.meta.url).href,
    mobile: new URL("./3os-r2-society-mobile.webp", import.meta.url).href,
  },
  finale: {
    desktop: new URL("./3os-r2-finale-desktop.webp", import.meta.url).href,
    mobile: new URL("./3os-r2-finale-mobile.webp", import.meta.url).href,
  },
};

/* --- 1 · Living Label ----------------------------------------------------- */
function mountLivingLabel(film) {
  if (film.dataset.llMounted === "true") return;
  const art = LIVING_LABEL[film.dataset.scene];
  if (!art) return;
  film.dataset.llMounted = "true";

  const layer = document.createElement("span");
  layer.className = "tos-ll";
  layer.dataset.tosLl = film.dataset.scene;
  layer.setAttribute("aria-hidden", "true");

  const picture = document.createElement("picture");
  const source = document.createElement("source");
  source.media = "(max-width: 700px)";
  source.srcset = art.mobile;

  const img = document.createElement("img");
  img.src = art.desktop;
  img.alt = "";
  img.loading = "lazy";
  img.decoding = "async";
  img.setAttribute("fetchpriority", "low");
  // A decorative master that fails to load must never leave a broken-image
  // glyph over a running film.
  img.addEventListener("error", () => layer.remove(), { once: true });

  picture.append(source, img);
  layer.append(picture);
  film.append(layer);
}

function setupLivingLabel() {
  const films = $$("[data-cinema-loop]").filter((film) => LIVING_LABEL[film.dataset.scene]);
  if (!films.length) return;

  if (!("IntersectionObserver" in window)) {
    films.forEach(mountLivingLabel);
    return;
  }

  // Mount roughly a quarter-viewport early: late enough that the film's own
  // source still wins the network, early enough that the label is decoded
  // before the frame reaches `playing`.
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        mountLivingLabel(entry.target);
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: "25% 0px", threshold: 0 },
  );

  films.forEach((film) => observer.observe(film));
}

/* --- 2 · Golden trace ----------------------------------------------------- */
const TRACE_HOSTS = [".scene__art--hero", ".scene__art--relay", ".scene__art--finale"];

function setupGoldenTrace() {
  const hosts = TRACE_HOSTS.map((selector) => document.querySelector(selector)).filter(Boolean);
  if (!hosts.length) return;

  const traces = hosts.map((host) => {
    const trace = document.createElement("span");
    trace.className = "tos-trace";
    trace.setAttribute("aria-hidden", "true");
    host.append(trace);
    return trace;
  });

  if (!("IntersectionObserver" in window)) {
    traces.forEach((trace) => trace.classList.add("is-drawn"));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-drawn");
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: "0px 0px -18% 0px", threshold: 0 },
  );

  traces.forEach((trace) => observer.observe(trace));
}

function boot() {
  setupLivingLabel();
  setupGoldenTrace();
}

/* R1C-M1-P1 — boot after first paint, own macrotask, same order */
scheduleBoot(boot);
